import { gql, WatchQueryFetchPolicy } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CreateScheduledEmailInput,
  EmailProvider,
  EmailRequestType,
  ScheduledEmail,
  UpdateScheduledEmailInput,
} from '@this+that/models';

import { FETCH_POLICY_CACHE_FIRST } from '../utils/constants';

export interface ScheduledEmailView extends ScheduledEmail {
  subject?: string;
  body?: string;
  additionalText?: string;
  toAddresses?: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  provider?: EmailProvider;
  accountId?: string;
  reqType?: EmailRequestType;
}

const SCHEDULED_EMAIL_FIELDS = gql`
  fragment ScheduledEmailFields on ScheduledEmail {
    userId
    scheduledEmailId
    status
    scheduledFor
    createdAt
    updatedAt
    lastError
    sentMessageId
    subject
    body
    additionalText
    toAddresses
    ccAddresses
    bccAddresses
    provider
    accountId
    reqType
  }
`;

export const GET_SCHEDULED_EMAILS = gql`
  ${SCHEDULED_EMAIL_FIELDS}
  query scheduledEmails {
    scheduledEmails {
      ...ScheduledEmailFields
    }
  }
`;

const CREATE_SCHEDULED_EMAIL = gql`
  ${SCHEDULED_EMAIL_FIELDS}
  mutation createScheduledEmail($input: CreateScheduledEmailInput!) {
    createScheduledEmail(input: $input) {
      ...ScheduledEmailFields
    }
  }
`;

const UPDATE_SCHEDULED_EMAIL = gql`
  ${SCHEDULED_EMAIL_FIELDS}
  mutation updateScheduledEmail($input: UpdateScheduledEmailInput!) {
    updateScheduledEmail(input: $input) {
      ...ScheduledEmailFields
    }
  }
`;

const DELETE_SCHEDULED_EMAIL = gql`
  mutation deleteScheduledEmail($scheduledEmailId: String!) {
    deleteScheduledEmail(scheduledEmailId: $scheduledEmailId)
  }
`;

interface ScheduledEmailsData {
  scheduledEmails: ScheduledEmailView[];
}

interface CreateScheduledEmailData {
  createScheduledEmail: ScheduledEmailView;
}

interface UpdateScheduledEmailData {
  updateScheduledEmail: ScheduledEmailView;
}

interface DeleteScheduledEmailData {
  deleteScheduledEmail: boolean;
}

const DEFAULT_SCHEDULED_EMAILS_FETCH_POLICY = 'cache-and-network';

export function useGetScheduledEmails(
  fetchPolicy: WatchQueryFetchPolicy = DEFAULT_SCHEDULED_EMAILS_FETCH_POLICY,
) {
  const { data, loading, error, refetch } = useQuery<ScheduledEmailsData>(
    GET_SCHEDULED_EMAILS,
    {
      fetchPolicy,
      nextFetchPolicy: FETCH_POLICY_CACHE_FIRST,
    },
  );

  return {
    scheduledEmails: data?.scheduledEmails ?? [],
    loading,
    error,
    refetch,
  };
}

export function useCreateScheduledEmail() {
  const [mutate, { loading, error }] = useMutation<CreateScheduledEmailData>(
    CREATE_SCHEDULED_EMAIL,
    {
      refetchQueries: [{ query: GET_SCHEDULED_EMAILS }],
    },
  );

  const createScheduledEmail = async (input: CreateScheduledEmailInput) => {
    const { data } = await mutate({ variables: { input } });
    return data?.createScheduledEmail ?? null;
  };

  return { createScheduledEmail, loading, error };
}

export function useUpdateScheduledEmail() {
  const [mutate, { loading, error }] = useMutation<UpdateScheduledEmailData>(
    UPDATE_SCHEDULED_EMAIL,
    {
      refetchQueries: [{ query: GET_SCHEDULED_EMAILS }],
    },
  );

  const updateScheduledEmail = async (input: UpdateScheduledEmailInput) => {
    const { data } = await mutate({ variables: { input } });
    return data?.updateScheduledEmail ?? null;
  };

  return { updateScheduledEmail, loading, error };
}

export function useDeleteScheduledEmail() {
  const [mutate, { loading, error }] = useMutation<DeleteScheduledEmailData>(
    DELETE_SCHEDULED_EMAIL,
    {
      refetchQueries: [{ query: GET_SCHEDULED_EMAILS }],
    },
  );

  const deleteScheduledEmail = async (scheduledEmailId: string) => {
    const { data } = await mutate({ variables: { scheduledEmailId } });
    return data?.deleteScheduledEmail ?? false;
  };

  return { deleteScheduledEmail, loading, error };
}
